import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { ref, onValue, remove } from "firebase/database";
import { db } from "../../../../../firebase";
import {
  Box,
  Button,
  Fade,
  Menu,
  MenuItem,
  useTheme,
  Modal,
  TextField,
  Grid,
  FormControl,
  Select,
  InputLabel,
  Paper,
  Popper,
  Typography,
  Stack,
  Tooltip,
} from "@mui/material";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import DeleteIcon from "@mui/icons-material/Delete";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { DataGrid } from "@mui/x-data-grid";
import { tokens } from "../../../../../theme";
import { dataGridStyle, ModalStyle } from "./TableBookinStyle";

import SnackBar from "../../../../../components/snackbar/SnackBar";
import { putBookingBusiness } from "../../../../../api/booking/bookingBusiness";
import {
  decodeAdminToken,
  getToken,
} from "../../../../../helpers/token/refreshToken";

function isOverflown(element) {
  return (
    element.scrollHeight > element.clientHeight ||
    element.scrollWidth > element.clientWidth
  );
}

const GridCellExpand = React.memo(function GridCellExpand(props) {
  const { width, value } = props;
  const wrapper = React.useRef(null);
  const cellDiv = React.useRef(null);
  const cellValue = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);

  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };

  const handleMouseLeave = () => {
    setShowFullCell(false);
  };

  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }

    function handleKeyDown(nativeEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === "Escape" || nativeEvent.key === "Esc") {
        setShowFullCell(false);
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);

  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: "center",
        lineHeight: "24px",
        width: 1,
        height: 1,
        position: "relative",
        display: "flex",
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: 1,
          width,
          display: "block",
          position: "absolute",
          top: 0,
        }}
      />
      <Box
        ref={cellValue}
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Box>
      {showPopper && (
        <Popper
          open={showFullCell && anchorEl !== null}
          anchorEl={anchorEl}
          style={{ width, marginLeft: -17 }}
        >
          <Paper
            elevation={1}
            style={{ minHeight: wrapper.current.offsetHeight - 3 }}
          >
            <Typography variant="body2" style={{ padding: 8 }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});

function getSubRoutesNames(subRoutes) {
  if (!subRoutes) return "";

  const routes = Array.isArray(subRoutes) ? subRoutes : Object.values(subRoutes);

  return routes
    .filter((subRoute) => subRoute?.name)
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0))
    .map((subRoute) => subRoute.name)
    .join("\n");
}

function getSubRoutesList(subRoutes) {
  if (!subRoutes) return [];

  const routes = Array.isArray(subRoutes) ? subRoutes : Object.values(subRoutes);

  return routes
    .filter((subRoute) => subRoute?.name)
    .sort((a, b) => Number(a?.id || 0) - Number(b?.id || 0));
}

function getRoutePoint(route) {
  if (!route) return "";

  const lat = route.lat ?? route.latitude;
  const lng = route.lng ?? route.longitude;

  if (lat && lng) return `${lat},${lng}`;
  return route.name || "";
}

function getGoogleMapsRouteUrl(origin, subRoutes, destination) {
  const waypointPoints = getSubRoutesList(subRoutes)
    .map(getRoutePoint)
    .filter(Boolean);
  const params = new URLSearchParams({
    api: "1",
    origin: getRoutePoint(origin),
    destination: getRoutePoint(destination),
  });

  if (waypointPoints.length > 0) {
    params.set("waypoints", waypointPoints.join("|"));
  }

  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

function openGoogleMapsRoute(origin, subRoutes, destination) {
  window.open(
    getGoogleMapsRouteUrl(origin, subRoutes, destination),
    "_blank",
    "noreferrer",
  );
}

const TableBookingBusiness = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const dataToken = getToken("adttkn");
  const { company_id } = decodeAdminToken(dataToken);

  const [booking, setBooking] = useState([]);
  const [openModal, setOpenModal] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [UID, setUID] = useState("");
  const [bookBill, setBookBill] = useState({});
  const [payment, setPayment] = useState("PAGADO");
  const [discount, setDiscount] = useState(0);

  const payment_type = {
    1: "EFECTIVO",
    2: "CREDITO",
  };

  const handleClose = () => setAnchorEl(null);

  const handleWhatsappClient = (booking, uuid) => {
    booking.map((book) => {
      if (book.uuid === uuid) {
        console.log(book);
        if (book.clientPhone) {
          if (book.driver) {
            window.open(
              `https://api.whatsapp.com/send?phone=${book.clientPhone}=&text=*Cliente:* ${book.clientName} ,%0A *Origen:* ${book.origin.name}%0A *Destino:* ${book.destination.name}%0A *Fecha:* ${book.date} - *Hora:* ${book.time}%0A *Pasajeros:* ${book.persons}%0A *Conductor:* ${book.driver.name}%0A *Marca:* ${book.driver.brand}%0A  *Carroceria:* ${book.driver.bodywork}%0A *Placa:* ${book.driver.plate}%0A *Color:* ${book.driver.color}`,
              "_blank",
              "noreferrer"
            );
          } else {
            window.open(
              `https://api.whatsapp.com/send?phone=${book.clientPhone}=&text=*Cliente:* ${book.clientName} ,%0A *Origen:* ${book.origin.name}%0A *Destino:* ${book.destination.name}%0A *Fecha:* ${book.date} - *Hora:* ${book.time}%0A *Pasajeros:* ${book.persons}`,
              "_blank",
              "noreferrer"
            );
          }
        } else {
          window.alert("no phone");
        }
      }
    });
    setAnchorEl(null);
  };

  const handleWhatsappDriver = (booking, uuid) => {
    booking.map((book) => {
      if (book.uuid === uuid) {
        if (book.driver) {
          window.open(
            `https://api.whatsapp.com/send?phone=${
              book.driver.phone
            }=&text=*Cliente:* ${book.clientName} ,%0A *Origen:* ${
              book.origin.name
            }%0A *Destino:* ${book.destination.name}%0A *Fecha:* ${
              book.date
            } - *Hora:* ${book.time}%0A *Pasajeros:* ${book.persons}%0A ${
              book.payment_type === 1
                ? `*Servicio:* EFECTIVO`
                : `*Servicio:* CREDITO`
            }%0A ${
              book.payment_type === 1
                ? `*Total Cobrar:* ${book.total}`
                : `*Total Cobrar:* ${book.price}`
            }`,
            "_blank",
            "noreferrer"
          );
        } else {
          window.alert("no phone");
        }
      }
    });
    setAnchorEl(null);
  };

  const handleOpenModal = (booking, uuid) => {
    booking.map((book) => {
      if (book.uuid === uuid) {
        setAnchorEl(null);
        setOpenModal(true);
        setBookBill(book);
        setPayment(book.payment_type);
      }
    });
  };
  const handleCloseModal = () => setOpenModal(false);

  useEffect(() => {
    const starCountRef = ref(db, "BookingBusiness/");
    onValue(starCountRef, (snapshot) => {
      const data = snapshot.val();
      setBooking([]);
      if (data !== null) {
        const compareByDate = (a, b) => {
          return b.created - a.created;
        };
        console.log(data);
        Object.values(data)
          .sort(compareByDate)
          .map((booking) => {
            if (
              booking.booking_type === 1
              // && booking.companyId == company_id
            ) {
              booking.originName = booking.origin.name;
              booking.originLat = booking.origin.lat;
              booking.originLng = booking.origin.lng;
              booking.destinationName = booking.destination.name;
              booking.destinationLat = booking.destination.lat;
              booking.destinationLng = booking.destination.lng;
              booking.payment_type = payment_type[booking.payment_type];
              setBooking((oldArray) => [...oldArray, booking]);
            }
          });
      }
    });
  }, []);

  GridCellExpand.propTypes = {
    value: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
  };

  function renderCellExpand(params) {
    return (
      <GridCellExpand
        value={params.value || ""}
        width={params.colDef.computedWidth}
      />
    );
  }

  renderCellExpand.propTypes = {
    colDef: PropTypes.object.isRequired,
    value: PropTypes.string.isRequired,
  };

  const columns = [
    {
      headerName: "Pedido",
      field: "counterBooking",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 70,
    },
    {
      headerName: "Fecha",
      field: "date",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 80,
    },
    {
      headerName: "Hora",
      field: "time",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 70,
      renderCell: ({
        row: {
          counterBooking,
          clientName,
          fullorigin,
          fulldestination,
          date,
          time,
          persons,
          note,
          clientPhone,
        },
      }) => {
        const url = `https://api.whatsapp.com/send?text=*ESTIMADO CLIENTE* ${clientName} %0A*PEDIDO:* ${counterBooking} ,%0A *Origen:* ${encodeURIComponent(
          fullorigin
        )}%0A*Destino:* ${encodeURIComponent(
          fulldestination
        )}%0A*Fecha:* ${date} - *Hora:* ${time}%0A*Pasajeros:* ${persons}%0A*NOTA:* ${note}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {time}
          </a>
        );
      },
    },
    {
      headerName: "Nombre",
      field: "clientName",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 180,
      renderCell: ({
        row: {
          counterBooking,
          clientName,
          fullorigin,
          fulldestination,
          date,
          time,
          persons,
          note,
          clientPhone,
        },
      }) => {
        const url = `https://api.whatsapp.com/send?phone=${clientPhone}&text=*ESTIMADO CLIENTE* ${clientName} %0A*PEDIDO:* ${counterBooking} ,%0A *Origen:* ${encodeURIComponent(
          fullorigin
        )}%0A*Destino:* ${encodeURIComponent(
          fulldestination
        )}%0A*Fecha:* ${date} - *Hora:* ${time}%0A*Pasajeros:* ${persons}%0A*NOTA:* ${note}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 14,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {clientName}
          </a>
        );
      },
    },
    {
      headerName: "Empresa",
      field: "enterpriseName",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 180,
    },
    {
      headerName: "Origen",
      field: "originName",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 200,
      renderCell: ({ row: { originName, originLat, originLng } }) => {
        const url = `https://maps.google.com/?q=${originLat},${originLng}`;
        return (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 12,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {originName}
          </a>
        );
      },
    },
    {
      headerName: "Destino",
      field: "destinationName",
      type: "string",
      headerAlign: "left",
      align: "left",
      width: 200,
      renderCell: ({
        row: {
          originName,
          originLat,
          originLng,
          destinationName,
          destinationLat,
          destinationLng,
          subRoutes,
          subRoutesList: rowSubRoutesList,
        },
      }) => {
        const url = `https://maps.google.com/?q=${destinationLat},${destinationLng}`;
        const routeSubRoutes = rowSubRoutesList ?? subRoutes;
        const subRoutesNames = getSubRoutesNames(routeSubRoutes);
        const subRoutesList = getSubRoutesList(routeSubRoutes);
        const origin = {
          name: originName,
          lat: originLat,
          lng: originLng,
        };
        const destination = {
          name: destinationName,
          lat: destinationLat,
          lng: destinationLng,
        };
        const destinationLink = (
          <a
            href={url}
            target="_blank"
            style={{
              color: "white",
              fontSize: 12,
              listStyle: "none",
              textDecoration: "none",
            }}
          >
            {destinationName}
          </a>
        );

        if (!subRoutesNames) return destinationLink;

        return (
          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
            <Tooltip
              title={
                <Typography fontSize={12} sx={{ whiteSpace: "pre-line" }}>
                  {subRoutesNames}
                </Typography>
              }
              arrow
            >
              {destinationLink}
            </Tooltip>
            <Button
              size="small"
              variant="contained"
              color="success"
              onClick={() =>
                openGoogleMapsRoute(origin, subRoutesList, destination)
              }
              sx={{
                minHeight: 28,
                px: 1,
                py: 0,
                fontSize: 11,
                textTransform: "none",
                whiteSpace: "nowrap",
              }}
            >
              Ver Ruta
            </Button>
          </Stack>
        );
      },
    },
    {
      headerName: "Pax",
      field: "persons",
      headerAlign: "left",
      align: "center",
      width: 20,
    },
    {
      headerName: "Notas",
      field: "note",
      width: 180,
      renderCell: renderCellExpand,
    },
    {
      headerName: "Conductor",
      field: "statusService",
      width: 90,
      renderCell: ({ row: { statusService } }) => {
        return (
          <div
            style={{
              fontSize: 14,
              width: "100%",
              textAlign: "right",
              color: "white",
              background: statusService === "PENDIENTE" ? "red" : "blue",
              borderRadius: 3,
              padding: 2,
            }}
          >
            {statusService}
          </div>
        );
      },
    },
    {
      headerName: "Estado",
      field: "statusPayment",
      width: 90,
      renderCell: ({ row: { statusPayment } }) => {
        return (
          <div
            style={{
              fontSize: 14,
              width: "100%",
              textAlign: "right",
              color: "white",
              background: statusPayment === "PENDIENTE" ? "red" : "blue",
              borderRadius: 3,
              padding: 2,
            }}
          >
            {statusPayment}
          </div>
        );
      },
    },
    {
      headerName: "Creado",
      field: "createBooking",
      width: 80,
    },
    {
      headerName: "Servicio",
      field: "payment_type",
      width: 80,
    },
    {
      headerName: "",
      field: "actions",
      width: 90,
      renderCell: ({ row: { uuid } }) => {
        return (
          <div>
            <Button
              color="secondary"
              aria-controls={open ? "fade-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={(event) => {
                setUID(uuid);
                setAnchorEl(event.currentTarget);
              }}
            >
              OPCIONES
            </Button>
            <Menu
              id="menu-options"
              MenuListProps={{ "aria-labelledby": "fade-button" }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              TransitionComponent={Fade}
            >
              <MenuItem
                onClick={() => {
                  setAnchorEl(null);
                  navigate(`/admin/add-booking-business/${UID}`);
                }}
              >
                <BorderColorIcon sx={{ color: "#FF0404" }} /> &nbsp;VER
              </MenuItem>
              <MenuItem
                onClick={(e) => {
                  handleWhatsappClient(booking, UID);
                }}
              >
                <WhatsAppIcon sx={{ color: "#FF0404" }} /> &nbsp;PASAJERO
              </MenuItem>

              <MenuItem
                onClick={(e) => {
                  handleWhatsappDriver(booking, UID);
                }}
              >
                <WhatsAppIcon sx={{ color: "#FF0404" }} /> &nbsp;CONDUCTOR
              </MenuItem>

              <MenuItem
                onClick={() => {
                  handleOpenModal(booking, UID);
                }}
              >
                FACTURA
              </MenuItem>

              <MenuItem
                onClick={async (e) => {
                  setOpenDeleteModal(true);
                  handleClose();
                }}
              >
                <DeleteIcon sx={{ color: "#FF0404" }} /> &nbsp;ELIMINAR
              </MenuItem>
            </Menu>
          </div>
        );
      },
    },
  ];

  const updateFacture = async (uuid, payment, discount) => {
    try {
      bookBill.payment_type = payment;
      bookBill.discount = discount;
      const token = "asdf";

      await putBookingBusiness(
        token,
        discount,
        payment,
        bookBill.counterBooking
      );

      const dbRef = ref(db, `BookingBusiness/${uuid}`);
      if (uuid != "") await remove(dbRef);
      handleCloseModal();
      setDiscount(0);
      setPayment("PAGADO");
      snackBarDialog("PEDIDO FACTURADO");
    } catch (error) {
      console.log(error);
      handleCloseModal();
      snackBarDialog("ERROR, NO SE GUARDO LOS CAMBIOS", "error");
    }
  };

  const [isLoading, setIsLoading] = useState(false);
  const [stateSnk, setStateSnk] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [messageSnk, setMessageSnk] = useState("");
  const vertical = "top";
  const horizontal = "center";

  const snackBarDialog = (message, severity = "success") => {
    setMessageSnk(message);
    setSeverity(severity);
    setStateSnk(true);
  };

  const handleCloseSnk = () => setStateSnk(false);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const closeDeleteModal = () => setOpenDeleteModal(false);

  const deleteQuote = async () => {
    setIsLoading(true);
    try {
      if (UID != "") await remove(ref(db, `BookingBusiness/${UID}`));

      setIsLoading(false);
      closeDeleteModal();
      snackBarDialog("COTIZACION ELIMINADA");
    } catch (error) {
      setIsLoading(false);
      console.log(error);
      snackBarDialog("ERROR: NO SE PUEDE ELIMINA LA COTIZACION", "error");
      closeDeleteModal();
    }
  };

  return (
    <Box>
      <Box m="10px" height="75vh" sx={dataGridStyle(colors)}>
        <DataGrid
          getRowId={(row) => row.uuid}
          rows={booking}
          columns={columns}
        />
      </Box>
      <Modal
        open={openModal}
        onClose={handleCloseModal}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            ...ModalStyle,
            width: 300,
            backgroundColor: colors.primary[400],
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} textAlign={"center"}>
              <h2 id="modal-title">FACTURA</h2>
            </Grid>
            <Grid item xs={12}>
              <TextField
                value={bookBill.counterBooking}
                fullWidth
                disabled
                label="Nro Pedido"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                value={bookBill.clientName}
                fullWidth
                disabled
                label="Client"
                variant="outlined"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                value={discount}
                fullWidth
                type={"number"}
                label="Descuento"
                variant="outlined"
                onChange={(e) => {
                  setDiscount(e.target.value);
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Tipo Pago</InputLabel>
                <Select
                  value={payment}
                  id="payment_type"
                  name="payment_type"
                  label="Tipo de Pago"
                  onChange={(e) => {
                    setPayment(e.target.value);
                  }}
                >
                  <MenuItem value={"PAGADO"}>PAGADO</MenuItem>
                  <MenuItem value={"CREDITO"}>CREDITO</MenuItem>
                  <MenuItem value={"CANCELADO"}>CANCELADO</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid
              item
              xs={12}
              sm={12}
              sx={{
                display: {
                  xs: payment === 2 ? "block" : "none",
                  md: payment === 2 ? "block" : "none",
                },
              }}
            >
              <Button
                fullWidth
                color="secondary"
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  //actualizar
                }}
              >
                Pago con tarjeta
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                color="secondary"
                variant="contained"
                onClick={(e) => {
                  e.preventDefault();
                  updateFacture(UID, payment, discount);
                }}
              >
                Guardar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      {/* MODAL ELIMINAR PEDIDO */}
      <Modal
        open={openDeleteModal}
        onClose={closeDeleteModal}
        aria-labelledby="modal-title"
        aria-describedby="delete-booking-business-now"
      >
        <Box
          sx={{
            ...ModalStyle,
            width: 300,
            backgroundColor: colors.primary[400],
          }}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} textAlign={"center"}>
              <h2 id="modal-title">ELIMINAR COTIZACION</h2>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                color="success"
                variant="contained"
                onClick={deleteQuote}
              >
                ACEPTAR
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                color="error"
                variant="contained"
                onClick={closeDeleteModal}
              >
                CANCELAR
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
      <SnackBar
        stateSnk={stateSnk}
        vertical={vertical}
        horizontal={horizontal}
        duration={4000}
        handleClose={handleCloseSnk}
        severity={severity}
        messageSnk={messageSnk}
      />
    </Box>
  );
};

export default TableBookingBusiness;
